
import { defineComponent, shallowRef, watch, watchEffect, onMounted, PropType } from 'vue'

// Composables
import { useSyncQueryParams } from '@/use/router'
import { UseDateRange } from '@/use/date-range'
import { UseSystems } from '@/tracing/system/use-systems'
import { createQueryEditor, UseUql } from '@/use/uql'
import { useGroups } from '@/tracing/use-explore-spans'

// Components
import ApiErrorCard from '@/components/ApiErrorCard.vue'
import PagedGroupsCard from '@/tracing/PagedGroupsCard.vue'

// Misc
import { AttrKey } from '@/models/otel'

export default defineComponent({
  name: 'TracingGroups',
  components: { ApiErrorCard, PagedGroupsCard },

  props: {
    dateRange: {
      type: Object as PropType<UseDateRange>,
      required: true,
    },
    systems: {
      type: Object as PropType<UseSystems>,
      required: true,
    },
    uql: {
      type: Object as PropType<UseUql>,
      required: true,
    },
    axiosParams: {
      type: Object as PropType<Record<string, any>>,
      required: true,
    },
    searchInput: {
      type: String,
      default: '',
    },
  },

  setup(props, ctx) {
    const groups = useGroups(() => {
      return props.axiosParams
    })
    const numGroup = shallowRef(0)

    const plottedColumns = shallowRef<string[]>()
    watchEffect(() => {
      if (!groups.plottableColumns.length) {
        plottedColumns.value = undefined
        return
      }

      if (!plottedColumns.value) {
        plottedColumns.value = groups.plottableColumns.slice(0, 1).map((col) => col.name)
        return
      }

      plottedColumns.value = plottedColumns.value.filter((colName) => {
        return groups.plottableColumns.findIndex((item) => item.name === colName) >= 0
      })
    })

    onMounted(() => {
      watch(
        () => props.systems.activeSystems,
        (activeSystems) => {
          if (!activeSystems.length) {
            return
          }
          if (props.uql.query) {
            return
          }
          props.uql.query = createQueryEditor()
            .exploreAttr(AttrKey.spanGroupId, props.systems.isSpan)
            .toString()
        },
        { immediate: true },
      )
    })

    useSyncQueryParams({
      fromQuery(queryParams) {
        props.dateRange.parseQueryParams(queryParams)
        props.systems.parseQueryParams(queryParams)
        groups.order.parseQueryParams(queryParams)

        if (!queryParams.has('query') && props.systems.activeSystems.length) {
          queryParams.set(
            'query',
            createQueryEditor().exploreAttr(AttrKey.spanGroupId, props.systems.isSpan).toString(),
          )
        }
        props.uql.parseQueryParams(queryParams)

        if (queryParams.has('column')) {
          plottedColumns.value = queryParams.array('column')
        } else {
          plottedColumns.value = undefined // accompanied with watchEffect
        }

        const search = queryParams.string('search')
        if (search) {
          ctx.emit('update:search-input', search)
        }
      },
      toQuery() {
        const queryParams: Record<string, any> = {
          ...props.dateRange.queryParams(),
          ...props.systems.queryParams(),
          ...props.uql.queryParams(),
          ...groups.order.queryParams(),
        }
        if (plottedColumns.value) {
          queryParams.column = plottedColumns.value.length ? plottedColumns.value : null
        }
        if (props.searchInput) {
          queryParams.search = props.searchInput
        }
        return queryParams
      },
    })

    watch(
      () => groups.queryInfo,
      (queryInfo) => {
        if (queryInfo) {
          props.uql.setQueryInfo(queryInfo)
        }
      },
    )

    return {
      groups,

      numGroup,
      plottedColumns,
    }
  },
})
